import { ComponentProps, useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { F0Box, F0Dialog } from '@factorialco/f0-react'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import {
  ApprovalRequestActivityDocument,
  ApprovalsMaterializedApprovalFlowHistoryDocument,
  RequestsForExternalUsersDocument,
} from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useActivity } from 'modules/core/lib/activities/activity'
import { encodeActivity } from 'modules/core/lib/activities/utils'
import { ActivityComponent } from 'modules/core/lib/activities/types'
import i18n from 'modules/core/lib/i18n'

import useBulkApproveRejectFlows from 'modules/approvals/hooks/useBulkApproveRejectFlows'
import ApprovalContent from 'modules/inbox_tasks/tasks/approval/ApprovalContent'
import { getResourceTextFromResourceType } from 'modules/inbox_tasks/tasks/approval/helpers'

type ModuleInfo = Omit<NonNullable<ComponentProps<typeof F0Dialog>['module']>, 'href'>

const RESOURCE_TYPE_TO_MODULE: Record<string, { id: ModuleInfo['id']; label: () => string }> = {
  'Timeoff::Leave': { id: 'timeoff', label: () => i18n.t('sidebar.time_off') },
  'Expenses::Expensable': { id: 'my_spending', label: () => i18n.t('sidebar.purchases') },
  'Attendance::OvertimeRequest': {
    id: 'time-tracking',
    label: () => i18n.t('sidebar.time_tracking'),
  },
  'Attendance::EditTimesheetRequest': {
    id: 'time-tracking',
    label: () => i18n.t('sidebar.time_tracking'),
  },
  'ShiftManagement::SwapShift': { id: 'shifts', label: () => i18n.t('sidebar.shift_management') },
  'Trainings::TrainingRequest': { id: 'my_trainings', label: () => i18n.t('sidebar.trainings') },
  'Ats::Requisition': { id: 'ats', label: () => i18n.t('sidebar.recruitment') },
  'Procurement::PurchaseRequest': { id: 'my_spending', label: () => i18n.t('sidebar.purchases') },
  'Banking::Card': { id: 'finance-treasury', label: () => i18n.t('sidebar.finance') },
  'Contracts::ContractVersion': { id: 'employees', label: () => i18n.t('sidebar.team') },
  'Contracts::ContractVersionFake': { id: 'employees', label: () => i18n.t('sidebar.team') },
}

const getModuleFromResourceType = (resourceType?: string | null): ModuleInfo | undefined => {
  if (!resourceType) return undefined
  const mapping = RESOURCE_TYPE_TO_MODULE[resourceType]
  if (!mapping) return undefined
  return { id: mapping.id, label: mapping.label() }
}

type Data = {
  materializedApprovalFlowId: string
}

export const ApprovalRequestActivity: ActivityComponent<Data> = ({
  materializedApprovalFlowId,
}) => {
  const { open, closeActivity } = useActivity()
  const navigate = useNavigate()
  const { search } = useLocation()
  const { access } = useCurrentSession()
  const { approve, disabled } = useBulkApproveRejectFlows()
  const apolloClient = useApolloClient()

  const { data, loading } = useQuery(ApprovalRequestActivityDocument, {
    variables: { id: materializedApprovalFlowId },
    skip: !open,
  })

  const flow = data?.approvals?.materializedApprovalsFlow

  const handleApprove = useCallback(() => {
    if (!flow?.id) return

    void approve(
      {
        accessId: access.id,
        materializedFlowId: flow.id,
      },
      () => {
        apolloClient.refetchQueries({
          include: [
            ApprovalRequestActivityDocument,
            ApprovalsMaterializedApprovalFlowHistoryDocument,
            RequestsForExternalUsersDocument,
          ],
        })
        closeActivity()
      }
    )
  }, [approve, access.id, flow?.id, closeActivity, apolloClient])

  const handleReject = useCallback(() => {
    navigate({
      search,
      hash: encodeActivity('core.rejectApprovalFlow', {
        materializedFlowId: materializedApprovalFlowId,
      }),
    })
  }, [navigate, search, materializedApprovalFlowId])

  const ownerName = useMemo(() => {
    const emp = flow?.owner?.employeeName
    if (!emp) return undefined
    const first = emp.preferredName || emp.firstName
    if (!first) return undefined
    return [first, emp.lastName].filter(Boolean).join(' ')
  }, [flow?.owner?.employeeName])

  const resourceText = flow?.resourceType ? getResourceTextFromResourceType(flow.resourceType) : ''
  const capitalizedResourceText = resourceText.charAt(0).toUpperCase() + resourceText.slice(1)

  const title = useMemo(() => {
    if (capitalizedResourceText && ownerName) {
      return i18n.t('inbox.approval_request.title_with_owner', {
        resource: capitalizedResourceText,
        name: ownerName,
      })
    }
    return i18n.t('inbox.approval_request.title')
  }, [capitalizedResourceText, ownerName])

  const module = useMemo(() => getModuleFromResourceType(flow?.resourceType), [flow?.resourceType])

  return (
    <F0Dialog
      isOpen={open}
      onClose={closeActivity}
      position="right"
      module={module}
      primaryAction={{
        label: i18n.t('inbox.approval_request.approve'),
        onClick: handleApprove,
        disabled: disabled || !flow?.id,
      }}
      secondaryAction={{
        label: i18n.t('inbox.approval_request.reject'),
        onClick: handleReject,
        disabled: !flow?.id,
      }}
    >
      {loading && !flow ? (
        <Loading />
      ) : (
        <>
          <ResourceHeader title={title} />
          <F0Box paddingTop="xl">
            <ApprovalContent
              approvable={flow?.approvable}
              owner={flow?.owner}
              reason={flow?.reason}
              resourceId={flow?.resourceId}
              resourceType={flow?.resourceType}
              status={flow?.status}
            />
          </F0Box>
        </>
      )}
    </F0Dialog>
  )
}
